// client/src/pages/PTO/ExportModal.tsx
// CSV export modal with date range, office, and entry type filters.

import React, { useCallback, useMemo, useState } from 'react';
import { Download, X } from 'lucide-react';
import type { PTOEntry, PTOEmployee, Office, EntryType } from './ptoTypes';
import { OFFICES, ENTRY_TYPES } from './ptoConstants';

// ─── Props ──────────────────────────────────────────────────────────────────

interface ExportModalProps {
  entries: PTOEntry[];
  employees: PTOEmployee[];
  onClose: () => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function formatDateForCSV(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${Number(m)}/${Number(d)}/${y}`;
}

// ─── Shared input styles ────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 13,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'rgba(255,255,255,.04)',
  color: 'var(--text)',
  outline: 'none',
  fontFamily: 'inherit',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--muted)',
  textTransform: 'uppercase',
  letterSpacing: '.5px',
  marginBottom: 6,
};

// ─── Component ──────────────────────────────────────────────────────────────

const ExportModal: React.FC<ExportModalProps> = ({
  entries,
  employees,
  onClose,
}) => {
  const [dateRange, setDateRange] = useState<'all' | 'custom'>('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [officeFilter, setOfficeFilter] = useState<Office | 'All'>('All');
  const [typeFilter, setTypeFilter] = useState<EntryType | 'All'>('All');

  // Picklists alphabetized at render time (constants stay in source order).
  const sortedOffices = useMemo(
    () => [...OFFICES].sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );
  const sortedEntryTypes = useMemo(
    () => [...ENTRY_TYPES].sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );

  const empMap = useMemo(() => {
    const m = new Map<string, PTOEmployee>();
    for (const e of employees) m.set(e.staffIndex, e);
    return m;
  }, [employees]);

  // Filter entries
  const filteredEntries = useMemo(() => {
    let filtered = entries;

    // Date range
    if (dateRange === 'custom') {
      if (fromDate) {
        filtered = filtered.filter((e) => e.entryDate >= fromDate);
      }
      if (toDate) {
        filtered = filtered.filter((e) => e.entryDate <= toDate);
      }
    }

    // Office
    if (officeFilter !== 'All') {
      const officeStaff = new Set(
        employees.filter((e) => e.office === officeFilter).map((e) => e.staffIndex),
      );
      filtered = filtered.filter((e) => officeStaff.has(e.staffIndex));
    }

    // Entry type
    if (typeFilter !== 'All') {
      filtered = filtered.filter((e) => e.entryType === typeFilter);
    }

    return filtered.sort((a, b) => a.entryDate.localeCompare(b.entryDate));
  }, [entries, employees, dateRange, fromDate, toDate, officeFilter, typeFilter]);

  const handleExport = useCallback(() => {
    const headers = ['Date', 'Employee', 'Office', 'Type', 'Hours', 'Notes', 'Source'];
    const rows = filteredEntries.map((e) => {
      const emp = empMap.get(e.staffIndex);
      return [
        formatDateForCSV(e.entryDate),
        escapeCSV(emp?.displayName ?? e.staffIndex),
        emp?.office ?? '',
        e.entryType,
        e.hours != null ? String(e.hours) : '',
        escapeCSV(e.note ?? ''),
        e.source,
      ].join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const officeSuffix = officeFilter === 'All' ? 'AllOffices' : officeFilter;
    const filename = `BMSS_PTO_2026_${officeSuffix}.csv`;

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [filteredEntries, empMap, officeFilter]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,.65)',
        backdropFilter: 'blur(4px)',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 460,
          background: 'linear-gradient(180deg, rgba(255,255,255,.04), rgba(255,255,255,.02))',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow)',
        }}
      >
        {/* ── Header ────────────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--border)',
            background: 'rgba(0,0,0,.12)',
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
            Export PTO Data
          </div>
          <button
            onClick={onClose}
            title="Close"
            style={{
              all: 'unset',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              borderRadius: 6,
              border: '1px solid var(--border)',
              color: 'var(--muted)',
              transition: 'background .15s, color .15s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,.06)';
              (e.currentTarget as HTMLElement).style.color = 'var(--text)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
              (e.currentTarget as HTMLElement).style.color = 'var(--muted)';
            }}
          >
            <X size={15} />
          </button>
        </div>

        {/* ── Body ──────────────────────────────────────────────── */}
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Date range */}
          <div>
            <label style={labelStyle}>Date Range</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setDateRange('all')}
                style={{
                  ...inputStyle,
                  cursor: 'pointer',
                  textAlign: 'center',
                  fontWeight: 600,
                  background:
                    dateRange === 'all'
                      ? 'rgba(122,162,255,.15)'
                      : 'rgba(255,255,255,.04)',
                  border:
                    dateRange === 'all'
                      ? '1px solid var(--accent)'
                      : '1px solid var(--border)',
                  color: dateRange === 'all' ? 'var(--accent)' : 'var(--muted)',
                  width: 'auto',
                  padding: '8px 16px',
                }}
              >
                All Year
              </button>
              <button
                onClick={() => setDateRange('custom')}
                style={{
                  ...inputStyle,
                  cursor: 'pointer',
                  textAlign: 'center',
                  fontWeight: 600,
                  background:
                    dateRange === 'custom'
                      ? 'rgba(122,162,255,.15)'
                      : 'rgba(255,255,255,.04)',
                  border:
                    dateRange === 'custom'
                      ? '1px solid var(--accent)'
                      : '1px solid var(--border)',
                  color: dateRange === 'custom' ? 'var(--accent)' : 'var(--muted)',
                  width: 'auto',
                  padding: '8px 16px',
                }}
              >
                Custom
              </button>
            </div>
            {dateRange === 'custom' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                <div>
                  <label style={{ ...labelStyle, fontSize: 10 }}>From</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    style={{
                      ...inputStyle,
                      colorScheme: 'dark',
                    }}
                  />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: 10 }}>To</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    style={{
                      ...inputStyle,
                      colorScheme: 'dark',
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Office filter */}
          <div>
            <label style={labelStyle}>Office</label>
            <select
              value={officeFilter}
              onChange={(e) => setOfficeFilter(e.target.value as Office | 'All')}
              style={{
                ...inputStyle,
                cursor: 'pointer',
                colorScheme: 'dark',
              }}
            >
              <option value="All">All Offices</option>
              {sortedOffices.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* Type filter */}
          <div>
            <label style={labelStyle}>Entry Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as EntryType | 'All')}
              style={{
                ...inputStyle,
                cursor: 'pointer',
                colorScheme: 'dark',
              }}
            >
              <option value="All">All Types</option>
              {sortedEntryTypes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Summary + export button */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: 8,
              borderTop: '1px solid var(--border)',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>
              {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'} to export
            </span>
            <button
              onClick={handleExport}
              disabled={filteredEntries.length === 0}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '9px 18px',
                fontSize: 13,
                fontWeight: 700,
                fontFamily: 'inherit',
                borderRadius: 8,
                border: 'none',
                cursor: filteredEntries.length === 0 ? 'not-allowed' : 'pointer',
                background:
                  filteredEntries.length === 0
                    ? 'rgba(122,162,255,.1)'
                    : 'var(--accent)',
                color:
                  filteredEntries.length === 0
                    ? 'rgba(122,162,255,.4)'
                    : '#0b0d10',
                transition: 'opacity .15s',
              }}
            >
              <Download size={14} />
              Export CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
